/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useProviderData.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for loading provider data.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useRef, useCallback } from 'react';

// Accepts plain integers OR UUID strings (xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)
const ID_PATTERN = /^(\d+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;
import { useNotifications } from '../../../contexts/NotificationContext';
import { useI18n } from '../../../hooks/useI18n';
import { getAiProviderById } from '../api/aiProvidersApi';

export interface AiProviderForm {
  id?: number;
  domainId: number;
  provider: string;
  name: string;
  apiKey: string;
  baseUrl: string;
  organization: string;
  azureApiKeyType: string;
  capabilities: string[];
  status: boolean;
}

export const DEFAULT_FORM: AiProviderForm = {
  domainId: 1,
  provider: '',
  name: '',
  apiKey: '',
  baseUrl: '',
  organization: '',
  azureApiKeyType: '',
  capabilities: [],
  status: true
};

export const PROVIDER_OPTIONS = [
  { value: 'openai', label: 'OpenAI' },
  { value: 'azure', label: 'Azure' },
  { value: 'elevenlabs', label: 'ElevenLabs' },
  { value: 'anthropic', label: 'Anthropic' },
  { value: 'google', label: 'Google' }
];

export const AZURE_KEY_TYPE_OPTIONS = [
  { value: 'openai', label: 'Azure OpenAI' },
  { value: 'speech', label: 'Azure Speech' }
];

export const GOOGLE_KEY_TYPE_OPTIONS = [
  { value: 'gemini', label: 'Google Gemini' },
  { value: 'speech', label: 'Google Speech' }
];

export function sanitizeForm(value: AiProviderForm): AiProviderForm {
  return {
    ...value,
    provider: (value.provider || '').trim(),
    name: (value.name || '').trim(),
    apiKey: (value.apiKey || '').trim(),
    baseUrl: (value.baseUrl || '').trim(),
    organization: (value.organization || '').trim(),
    azureApiKeyType: (value.azureApiKeyType || '').trim(),
    capabilities: Array.isArray(value.capabilities) ? value.capabilities : [],
    status: Boolean(value.status)
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapApiToForm(payload: any): AiProviderForm {
  return sanitizeForm({
    id: payload.id,
    domainId: payload.domain_id ?? DEFAULT_FORM.domainId,
    provider: payload.provider ?? DEFAULT_FORM.provider,
    name: payload.name ?? DEFAULT_FORM.name,
    apiKey: payload.api_key ?? DEFAULT_FORM.apiKey,
    baseUrl: payload.base_url ?? DEFAULT_FORM.baseUrl,
    organization: payload.organization ?? DEFAULT_FORM.organization,
    azureApiKeyType: payload.api_key_type ?? DEFAULT_FORM.azureApiKeyType,
    capabilities: Array.isArray(payload.capabilities) ? payload.capabilities : DEFAULT_FORM.capabilities,
    status: payload.status ?? DEFAULT_FORM.status
  });
}

export function useProviderData(id: string | undefined, duplicateId: string | null, resetParam?: string | null) {
  const { showError, showInfo } = useNotifications();
  const { t } = useI18n();

  const [formData, setFormData] = useState<AiProviderForm>(() => ({ ...DEFAULT_FORM }));
  const [baseline, setBaseline] = useState<AiProviderForm | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const providerRequestRef = useRef<AbortController | null>(null);
  const providerLoadedRef = useRef<string | null>(null);
  const formResetRef = useRef(false);
  const lastIdRef = useRef<string | undefined>(id);
  const resetParamProcessedRef = useRef<string | null>(null);

  const showInfoRef = useRef(showInfo);
  const showErrorRef = useRef(showError);
  const translateRef = useRef(t);
  const setFormDataRef = useRef(setFormData);
  const setBaselineRef = useRef(setBaseline);

  useEffect(() => {
    showInfoRef.current = showInfo;
    showErrorRef.current = showError;
    translateRef.current = t;
    setFormDataRef.current = setFormData;
    setBaselineRef.current = setBaseline;
  }, [showInfo, showError, t, setFormData, setBaseline]);

  useEffect(() => {
    const loadId = duplicateId || id;
    const idChangedToNew = lastIdRef.current && lastIdRef.current !== 'new' && (id === 'new' || id === undefined);
    const shouldReset = resetParam && resetParam !== resetParamProcessedRef.current;

    // Reset form if navigating to 'new' or resetParam is present
    if ((id === 'new' || id === undefined || idChangedToNew || shouldReset) && !duplicateId) {
      if (shouldReset) {
        resetParamProcessedRef.current = resetParam;
      }
      const fresh = sanitizeForm(DEFAULT_FORM);
      setFormDataRef.current(fresh);
      setBaselineRef.current(null);
      providerLoadedRef.current = null;
      lastIdRef.current = id;
      setIsLoading(false);
      return;
    }

    // Reset loaded ref when id changes
    if (providerLoadedRef.current !== loadId) {
      providerLoadedRef.current = null;
    }

    lastIdRef.current = id;

    if (loadId && loadId !== 'new') {
      if (!ID_PATTERN.test(loadId)) {
        showErrorRef.current?.(translateRef.current('aiProviders.messages.loadError'));
        setIsLoading(false);
        return;
      }

      // Prevent loading if already loaded
      if (providerLoadedRef.current === loadId) {
        setIsLoading(false);
        return;
      }

      providerLoadedRef.current = loadId;

      // Fetch provider
      providerRequestRef.current?.abort();
      const controller = new AbortController();
      providerRequestRef.current = controller;

      setIsLoading(true);

      getAiProviderById(loadId)
        .then(response => {
          if (providerRequestRef.current !== controller) return;

          const payload = response.data;
          const isDuplicate = Boolean(duplicateId);
          const mapped = mapApiToForm(payload ?? {});

          if (isDuplicate) {
            // For duplicate, remove id and append _copy to name
            const duplicated = {
              ...mapped,
              id: undefined,
              name: mapped.name ? `${mapped.name}_copy` : ''
            };
            setFormDataRef.current(duplicated);
            setBaselineRef.current(null);
            showInfoRef.current?.(translateRef.current('aiProviders.messages.duplicateHint'));
          } else {
            setFormDataRef.current(mapped);
            setBaselineRef.current(mapped);
          }
          setIsLoading(false);
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .catch((error: any) => {
          if (providerRequestRef.current !== controller) return;

          const isAbort =
            error?.name === 'AbortError' ||
            (error instanceof DOMException && error.name === 'AbortError');

          if (!isAbort) {
            showErrorRef.current?.(translateRef.current('aiProviders.messages.loadError'));
          }
          setIsLoading(false);
        })
        .finally(() => {
          if (providerRequestRef.current === controller) {
            providerRequestRef.current = null;
          }
        });
    } else {
      // Reset form for new provider
      const fresh = sanitizeForm(DEFAULT_FORM);
      setFormDataRef.current(fresh);
      setBaselineRef.current(null);
      setIsLoading(false);
    }

    return () => {
      providerRequestRef.current?.abort();
    };
  }, [id, duplicateId, resetParam]);

  return {
    formData,
    setFormData,
    baseline,
    setBaseline,
    isLoading
  };
}

